import { jsPDF } from 'jspdf';

import { IPatientDetails } from 'src/features/patient/domain/types';
import { IInferenceResult } from 'src/features/study/domain/types';

// ── Constants ────────────────────────────────────────────────────────────────

const PAGE_W = 210; // A4 width mm
const PAGE_H = 297; // A4 height mm
const MARGIN = 15;
const CONTENT_W = PAGE_W - 2 * MARGIN; // 180mm

const COL_GAP = 6; // gap between the 2 image columns
const IMG_COL_W = (CONTENT_W - COL_GAP) / 2; // ~87mm
const IMG_H = IMG_COL_W * 0.65; // ~56mm — maintains reasonable proportion

const BRAND_RED: [number, number, number] = [190, 51, 39];
const ORANGE: [number, number, number] = [230, 81, 0];
const GRAY_DARK: [number, number, number] = [40, 40, 40];
const GRAY_MID: [number, number, number] = [110, 110, 110];
const GRAY_LIGHT: [number, number, number] = [220, 220, 220];

// ── Helpers ──────────────────────────────────────────────────────────────────

const ensureSpace = (doc: jsPDF, y: number, needed: number): number => {
	if (y + needed > PAGE_H - MARGIN) {
		doc.addPage();
		return MARGIN;
	}
	return y;
};

const drawHRule = (doc: jsPDF, y: number): void => {
	doc.setDrawColor(...GRAY_LIGHT);
	doc.setLineWidth(0.3);
	doc.line(MARGIN, y, PAGE_W - MARGIN, y);
};

// ── Main export ───────────────────────────────────────────────────────────────

export const generateAIReportPDF = (
	patientDetails: IPatientDetails,
	inferenceResults: IInferenceResult[],
	patientStudyId: string,
): void => {
	const doc = new jsPDF({
		orientation: 'portrait',
		unit: 'mm',
		format: 'a4',
	});

	let y = MARGIN;

	// ── Page header ───────────────────────────────────────────────────────────

	doc.setFontSize(18);
	doc.setFont('helvetica', 'bold');
	doc.setTextColor(...GRAY_DARK);
	doc.text('AI Inference Report', MARGIN, y);

	doc.setFontSize(9);
	doc.setFont('helvetica', 'normal');
	doc.setTextColor(...GRAY_MID);
	const now = new Date();
	const dateStr = now.toLocaleDateString('en-GB', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
	});
	const timeStr = now.toLocaleTimeString('en-US', {
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hour12: true,
	});
	doc.text(`Generated on ${dateStr} at ${timeStr}`, PAGE_W - MARGIN, y, {
		align: 'right',
	});

	y += 8;
	drawHRule(doc, y);
	y += 8;

	// ── Patient information ───────────────────────────────────────────────────

	doc.setFontSize(11);
	doc.setFont('helvetica', 'bold');
	doc.setTextColor(...GRAY_DARK);
	doc.text(
		`${patientDetails.first_name} ${patientDetails.last_name}`,
		MARGIN,
		y,
	);

	doc.setFontSize(8);
	doc.setFont('helvetica', 'normal');
	doc.setTextColor(...GRAY_MID);
	doc.text(`Patient ID: ${patientDetails.site_patient_id}`, MARGIN, y + 5);

	y += 12;

	// Patient info — 2-column grid
	const patientFields: [string, string][] = [
		['Gender', patientDetails.gender || '-'],
		['Age', patientDetails.age != null ? String(patientDetails.age) : '-'],
		['Contact', patientDetails.contact_number || '-'],
		['Occupation', patientDetails.occupation || '-'],
		['Address', patientDetails.address || '-'],
	];

	const FIELD_COL_W = CONTENT_W / 2;
	const FIELD_ROW_H = 9;

	patientFields.forEach(([label, value], idx) => {
		const col = idx % 2;
		const row = Math.floor(idx / 2);
		const x = MARGIN + col * FIELD_COL_W;
		const fieldY = y + row * FIELD_ROW_H;

		doc.setFontSize(7.5);
		doc.setFont('helvetica', 'bold');
		doc.setTextColor(...GRAY_MID);
		doc.text(label.toUpperCase(), x, fieldY);

		doc.setFontSize(8.5);
		doc.setFont('helvetica', 'normal');
		doc.setTextColor(...GRAY_DARK);
		doc.text(value, x, fieldY + 4);
	});

	y += Math.ceil(patientFields.length / 2) * FIELD_ROW_H + 6;

	drawHRule(doc, y);
	y += 10;

	// ── Inference results ─────────────────────────────────────────────────────

	for (const result of inferenceResults) {
		// Label heading
		y = ensureSpace(doc, y, 14);

		doc.setFontSize(11);
		doc.setFont('helvetica', 'bold');
		doc.setTextColor(...GRAY_DARK);
		doc.text(result.label, MARGIN, y);
		y += 8;

		const models = Object.entries(result.inference_results);

		for (let i = 0; i < models.length; i += 2) {
			const pair = models.slice(i, i + 2) as [
				string,
				(typeof models)[0][1],
			][];

			// Estimate cell height: model name + image + detection lines
			const MAX_DETECT_LINES = 3;
			const DETECT_LINE_H = 4;
			const cellH = 5 + IMG_H + 3 + MAX_DETECT_LINES * DETECT_LINE_H + 8;
			y = ensureSpace(doc, y, cellH);

			pair.forEach(([modelName, modelResult], pairIdx) => {
				const x = MARGIN + pairIdx * (IMG_COL_W + COL_GAP);

				// Model name label (styled like a chip)
				doc.setFontSize(8);
				doc.setFont('helvetica', 'bold');
				doc.setTextColor(...BRAND_RED);
				doc.text(modelName, x, y);

				const inferenceLabel = `${modelResult.inference_time_ms.toFixed(0)} ms`;
				doc.setFontSize(7);
				doc.setFont('helvetica', 'normal');
				doc.setTextColor(...GRAY_MID);
				doc.text(inferenceLabel, x + IMG_COL_W, y, { align: 'right' });

				const imgY = y + 4;

				// Annotated image
				try {
					doc.addImage(
						modelResult.annotated_image,
						'PNG',
						x,
						imgY,
						IMG_COL_W,
						IMG_H,
					);
				} catch {
					// Fallback placeholder if image fails
					doc.setFillColor(240, 240, 240);
					doc.rect(x, imgY, IMG_COL_W, IMG_H, 'F');
					doc.setFontSize(7);
					doc.setTextColor(...GRAY_MID);
					doc.text(
						'Image unavailable',
						x + IMG_COL_W / 2,
						imgY + IMG_H / 2,
						{
							align: 'center',
						},
					);
				}

				// Detections below image
				const detectY = imgY + IMG_H + 4;
				doc.setFontSize(7.5);
				doc.setFont('helvetica', 'normal');

				if (modelResult.detections.length === 0) {
					doc.setTextColor(...GRAY_MID);
					doc.setFont('helvetica', 'italic');
					doc.text('No detections', x, detectY);
				} else {
					const detectionText = modelResult.detections
						.map(
							(d) =>
								`${d.class} ${(d.confidence * 100).toFixed(1)}%`,
						)
						.join('   ');

					const lines = doc.splitTextToSize(detectionText, IMG_COL_W);
					const capped = lines.slice(0, MAX_DETECT_LINES) as string[];
					doc.setTextColor(...ORANGE);
					doc.text(capped, x, detectY);
				}
			});

			y += 5 + IMG_H + 3 + MAX_DETECT_LINES * DETECT_LINE_H + 6;
		}

		// Spacer + rule between result groups
		y = ensureSpace(doc, y, 6);
		drawHRule(doc, y);
		y += 8;
	}

	// ── Footer on every page ──────────────────────────────────────────────────

	const totalPages = doc.getNumberOfPages();
	for (let p = 1; p <= totalPages; p++) {
		doc.setPage(p);
		doc.setFontSize(7);
		doc.setFont('helvetica', 'normal');
		doc.setTextColor(...GRAY_MID);
		doc.text(`Page ${p} of ${totalPages}`, PAGE_W - MARGIN, PAGE_H - 8, {
			align: 'right',
		});
	}

	doc.save(`ai-report-${patientStudyId}.pdf`);
};
